import { useState } from "preact/hooks";
import type { CoreData, CountryId, Decision, SourceInfo } from "../data/types";
import { COUNTRIES, COUNTRY_INFO } from "../data/countries";
import { useStore } from "../state/store";
import { PALETTES, SITE, ask, decisions, ratings, replaceDecisions, settings, shareOrCopy, toast, ui, type Theme } from "../state/app";
import { isPaired, partnerSets, restoreFrom } from "../state/partner";
import { chosen, clearChosen, syncReminder } from "../state/chosen";
import { nameKey } from "../data/keys";
import { closeOverlay } from "../state/router";
import { DATA_BASE } from "../data/load";
import { fmt } from "../data/format";
import { Sheet } from "./Sheet";
import { Segmented } from "./FilterSheet";
import { saveFile } from "./saveFile";
import { IconDownload, IconShare, IconStar, IconUpload } from "./icons";
import type { SexPref } from "../data/types";
import { onRadioKey, rovingTab } from "./radio";

const SHARE_APP_TEXT = "Nameling: swipe through baby names from the official records, and find one you both love.";
/** the App Store page once there is one, otherwise the website (which works on any phone) */
const APP_LINK = __APP_STORE_ID__ ? `https://apps.apple.com/app/id${__APP_STORE_ID__}` : SITE;

/** The preview can't serve a .zip, so its build ships the dataset as base64 text. */
function DatasetButton({ cc }: { cc: "uk" | "us" }) {
  const label = (
    <>
      <IconDownload size={16} /> Download the {COUNTRY_INFO[cc].short} dataset (CSV)
    </>
  );
  // the iOS app doesn't carry the CSV files, and the US one is too big for the preview host
  if ((__PREVIEW__ && cc === "us") || __NATIVE__)
    return (
      <a class="btn btn-small" href={`${SITE}/data/${cc}-baby-names.zip`} target="_blank" rel="noopener">
        {label}
      </a>
    );
  if (__PREVIEW__)
    return (
      <button class="btn btn-small" onClick={() => saveDataset(cc)}>
        {label}
      </button>
    );
  return (
    <a class="btn btn-small" href={`${DATA_BASE.replace(/\/[^/]+$/, "")}/${cc}-baby-names.zip`} download>
      {label}
    </a>
  );
}

async function saveDataset(cc: CountryId): Promise<void> {
  try {
    const res = await fetch(`data/${cc}-baby-names.zip.b64.txt`);
    if (!res.ok) throw new Error();
    const bin = atob((await res.text()).trim());
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    await saveFile(`${cc}-baby-names.zip`, new Blob([bytes], { type: "application/zip" }));
  } catch {
    toast("Couldn't load the dataset just now");
  }
}

const UK_SOURCES: SourceInfo[] = [
  { id: "ons", title: "Baby names in England and Wales", publisher: "Office for National Statistics", url: "https://www.ons.gov.uk/peoplepopulationandcommunity/birthsdeathsandmarriages/livebirths", licence: "Open Government Licence v3.0" },
  { id: "nrs", title: "Babies' first names", publisher: "National Records of Scotland", url: "https://www.nrscotland.gov.uk/", licence: "Open Government Licence v3.0" },
  { id: "nisra", title: "Baby names", publisher: "Northern Ireland Statistics and Research Agency", url: "https://www.nisra.gov.uk/", licence: "Open Government Licence v3.0" },
];

const US_SOURCES: SourceInfo[] = [
  { id: "ssa", title: "Popular Baby Names", publisher: "US Social Security Administration", url: "https://www.ssa.gov/oact/babynames/", licence: "Public domain" },
];

function AboutData({ core }: { core: CoreData }) {
  const c = core.country;
  // the data names its sources (the US record from data releases since late 2026); older releases fall back
  const listed = core.sources.length ? core.sources : c.id === "us" ? US_SOURCES : UK_SOURCES;
  const sources = c.id === "other" && !listed.some((s) => s.id === "ssa") ? [...listed, ...US_SOURCES] : listed;
  return (
    <section class="fsec about">
      <h3>About the data</h3>
      {c.id === "uk" && (
        <p>
          Every count comes from official birth registrations: the names given to babies registered in each year. Nameling
          adds the three nations' lists together into UK totals and ranks.
        </p>
      )}
      {c.id === "us" && (
        <p>
          Every count comes from the Social Security Administration's national list: the names on Social Security card
          applications for babies born in the US, from {c.since}.
        </p>
      )}
      {c.id === "other" && (
        <p>
          For families outside the UK and US, Nameling adds the two countries' official records together: birth
          registrations in England and Wales, Scotland and Northern Ireland, and the names on US Social Security card
          applications, from {c.since}, when both begin.
        </p>
      )}
      {sources.length > 0 && (
        <ul class="sources">
          {sources.map((src) => (
            <li key={src.id}>
              <a href={src.url} target="_blank" rel="noopener">
                {src.title}
              </a>
              , {src.publisher}
              {src.years ? ` (${src.years})` : ""}. {src.licence}.
            </li>
          ))}
        </ul>
      )}
      <p class="fhint">
        {c.id === "uk" &&
          "England and Wales and Northern Ireland list names given to at least 3 babies in a year, and so does Scotland from 2024. Contains public sector information licensed under the Open Government Licence v3.0. "}
        {c.id === "us" && `Names given to fewer than ${c.minListed} babies of either sex in a year aren't published. `}
        {c.id === "other" &&
          "The UK lists names given to at least 3 babies in a year, the US 5. Contains public sector information licensed under the Open Government Licence v3.0. "}
        Meanings credited to Wiktionary are shared under CC BY-SA 4.0. Nameling is independent, and isn't affiliated
        with or endorsed by any of the organisations whose records it uses.
      </p>
      {/* "other" is the two records added together, so it offers both; the iPhone app leaves the
          downloads to the website */}
      {!__NATIVE__ && (
        <div class="row-gap wrap">
          {(c.id === "other" ? (["uk", "us"] as const) : [c.id as "uk" | "us"]).map((cc) => (
            <DatasetButton key={cc} cc={cc} />
          ))}
        </div>
      )}
      <p class="fhint">
        Data {c.since} to {core.latest}, built {core.built} · app {typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev"}
      </p>
      <p class="fhint links">
        {[
          ["privacy.html", "Privacy"],
          ["support.html", "Help"],
          ["support.html#contact", "Contact"],
          ["licences.html", "Licences"],
        ].map(([path, label], i) => (
          <span key={path}>
            {i > 0 && " · "}
            <a href={`${__NATIVE__ || __PREVIEW__ ? SITE : ""}/${path}`} target="_blank" rel="noopener">
              {label}
            </a>
          </span>
        ))}
      </p>
    </section>
  );
}

export function SettingsSheet({ core, open }: { core: CoreData; open: boolean }) {
  const s = useStore(settings, (x) => x);
  const switching = useStore(ui, (x) => x.switching);
  const map = useStore(decisions, (x) => x.map);
  const [restore, setRestore] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Object.keys(map).length;
  const hasPartner = useStore(partnerSets, (x) => x.others.length > 0);

  const exportData = () => {
    const rows = Object.entries(map)
      .map(([k, d]) => ({ name: core.byKey.get(k)?.name ?? d.n ?? k, key: k, verdict: d.d === 2 ? "love" : d.d === 1 ? "like" : "nope", at: new Date(d.t).toISOString() }))
      .sort((a, b) => a.at.localeCompare(b.at));
    // the chosen name goes too; older versions of the app ignore it when importing
    const json = JSON.stringify(
      { app: "nameling", version: 1, settings: { sex: s.sex, surname: s.surname, myName: s.myName }, decisions: rows, ratings: ratings.get(), chosen: chosen.get().key ? chosen.get() : undefined },
      null,
      2,
    );
    saveFile(`nameling-${new Date().toISOString().slice(0, 10)}.json`, new Blob([json], { type: "application/json" }));
  };

  const importData = async (file: File) => {
    try {
      const data = JSON.parse(await file.text());
      if (data.app !== "nameling" || !Array.isArray(data.decisions)) throw new Error();
      const merged: Record<string, Decision> = { ...map };
      let added = 0;
      for (const r of data.decisions) {
        const d = r.verdict === "love" ? 2 : r.verdict === "like" ? 1 : r.verdict === "nope" ? -1 : 0;
        const key = typeof r.key === "string" ? nameKey(r.key) : "";
        if (!d || !key) continue;
        const t = Date.parse(r.at) || Date.now();
        if (!merged[key] || merged[key].t < t) {
          merged[key] = { d: d as Decision["d"], t };
          added++;
        }
      }
      replaceDecisions(merged);
      if (data.ratings?.elo) ratings.set({ pairs: {}, ...data.ratings });
      // a chosen name comes across unless this phone already has one
      const c = data.chosen;
      if (c && typeof c.key === "string" && nameKey(c.key) === c.key && c.key && typeof c.name === "string" && !chosen.get().key) {
        const text = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
        // the due date and birth details already on this phone (kept by "Choose a different
        // name") stay; the file only fills in what's blank
        const here = chosen.get();
        const date = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "");
        chosen.set({
          key: c.key,
          name: text(c.name, 60),
          middle: text(c.middle, 40),
          at: Number(c.at) || Date.now(),
          due: here.due || date(c.due),
          bornOn: here.bornOn || date(c.bornOn),
          bornAt: here.bornAt || (typeof c.bornAt === "string" && /^\d{2}:\d{2}$/.test(c.bornAt) ? c.bornAt : ""),
          weight: here.weight || text(c.weight, 20),
        });
        void syncReminder();
      }
      toast(`Imported ${fmt(added)} swipe${added === 1 ? "" : "s"}`);
    } catch {
      toast("That file isn't a Nameling export.");
    }
  };

  return (
    <Sheet open={open} onClose={closeOverlay} title="Settings">
      {COUNTRIES.length > 1 && (
        <section class="fsec">
          <h3>Country</h3>
          <Segmented<CountryId>
            label="Country"
            value={s.country}
            onChange={(v) => settings.set({ country: v })}
            options={COUNTRIES.map((c) => ({ v: c, label: COUNTRY_INFO[c].short }))}
          />
          <p class="fhint">
            {switching
              ? `Loading names from ${COUNTRY_INFO[switching].name}…`
              : `${COUNTRY_INFO[s.country].blurb}. Your swipes stay as they are when you switch.`}
          </p>
        </section>
      )}

      <section class="fsec">
        <h3>About you</h3>
        <label class="field">
          <span>Your first name</span>
          <input
            type="text"
            value={s.myName}
            maxLength={40}
            placeholder="Shown to your partner"
            onInput={(ev) => settings.set({ myName: (ev.target as HTMLInputElement).value })}
          />
        </label>
        <label class="field">
          <span>Baby's surname</span>
          <input
            type="text"
            value={s.surname}
            maxLength={40}
            placeholder="To try out full names"
            onInput={(ev) => settings.set({ surname: (ev.target as HTMLInputElement).value })}
          />
        </label>
        <h3>Names for</h3>
        <Segmented<SexPref>
          label="Names for"
          value={s.sex}
          onChange={(v) => settings.set({ sex: v })}
          options={[
            { v: "F", label: "A girl" },
            { v: "M", label: "A boy" },
            { v: "X", label: "Either" },
          ]}
        />
        <label class="toggle-row">
          <span>
            <span class="toggle-label">Learn from my likes</span>
            <span class="toggle-hint">Now and then, show names like the ones you've liked</span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={s.tastePicks !== false}
            onChange={(ev) => settings.set({ tastePicks: (ev.target as HTMLInputElement).checked })}
          />
          <span class="switch" aria-hidden="true" />
        </label>
      </section>

      <section class="fsec">
        <h3>Appearance</h3>
        <div class="swatches" role="radiogroup" aria-label="Colours">
          {PALETTES.map((p, i) => (
            <button
              key={p.id}
              role="radio"
              aria-checked={s.palette === p.id}
              tabIndex={rovingTab(i, s.palette === p.id, PALETTES.some((x) => x.id === s.palette))}
              class={`swatch ${s.palette === p.id ? "on" : ""}`}
              onClick={() => settings.set({ palette: p.id })}
              onKeyDown={(ev) => onRadioKey(ev, i, PALETTES.length, (j) => settings.set({ palette: PALETTES[j].id }))}
            >
              <span class="swatch-dot" style={{ background: p.swatch }} aria-hidden="true" />
              {p.label}
            </button>
          ))}
        </div>
        <Segmented<Theme>
          label="Theme"
          value={s.theme}
          onChange={(v) => settings.set({ theme: v })}
          options={[
            { v: "system", label: "Auto" },
            { v: "light", label: "Light" },
            { v: "dark", label: "Dark" },
          ]}
        />
        <label class="toggle-row">
          <span>
            <span class="toggle-label">{__NATIVE__ ? "Haptics" : "Vibrate on swipe"}</span>
            <span class="toggle-hint">{__NATIVE__ ? "A light tap as you swipe and match" : "On phones that support it"}</span>
          </span>
          <input type="checkbox" role="switch" checked={s.haptics} onChange={(ev) => settings.set({ haptics: (ev.target as HTMLInputElement).checked })} />
          <span class="switch" aria-hidden="true" />
        </label>
        <label class="toggle-row">
          <span>
            <span class="toggle-label">Reduce motion</span>
            <span class="toggle-hint">Cards fade instead of flying off, with no confetti or sliding panels</span>
          </span>
          <input type="checkbox" role="switch" checked={s.reduceMotion} onChange={(ev) => settings.set({ reduceMotion: (ev.target as HTMLInputElement).checked })} />
          <span class="switch" aria-hidden="true" />
        </label>
      </section>

      {!isPaired() && !__PREVIEW__ && (
        <section class="fsec">
          <h3>Restore from another phone</h3>
          <p class="fhint">Paste the restore code from the Together screen on your old phone.</p>
          <form
            class="inline-form"
            onSubmit={async (ev) => {
              ev.preventDefault();
              setBusy(true);
              try {
                const added = await restoreFrom(restore);
                toast(`Restored ${fmt(added)} swipes and your pairing`);
                setRestore("");
              } catch (e) {
                toast((e as Error).message || "Couldn't restore from that code");
              } finally {
                setBusy(false);
              }
            }}
          >
            <input
              type="text"
              value={restore}
              placeholder="NML1.…"
              aria-label="Restore code"
              autocomplete="off"
              spellcheck={false}
              onInput={(ev) => setRestore((ev.target as HTMLInputElement).value)}
            />
            <button class="btn" disabled={busy || !restore.trim()}>
              Restore
            </button>
          </form>
        </section>
      )}

      {!__PREVIEW__ && (
        <section class="fsec">
          <h3>Privacy</h3>
          <label class="toggle-row">
            <span>
              <span class="toggle-label">Send anonymous usage counts</span>
              <span class="toggle-hint">
                Totals such as how many names are swiped, and reports of errors. Nothing that identifies you or the names
                you choose.
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              checked={s.shareUsage}
              onChange={(ev) => settings.set({ shareUsage: (ev.target as HTMLInputElement).checked, usageAsked: true })}
            />
            <span class="switch" aria-hidden="true" />
          </label>
        </section>
      )}

      {!__PREVIEW__ && (
        <section class="fsec">
          <h3>Tell a friend</h3>
          <p class="fhint">Know someone who's expecting? Nameling is free, with no account to make.</p>
          <div class="row-gap wrap">
            <button
              class="btn btn-small"
              onClick={() =>
                shareOrCopy(
                  { title: "Nameling", text: SHARE_APP_TEXT, url: APP_LINK },
                  `${SHARE_APP_TEXT} ${APP_LINK}`,
                  "Link copied",
                )
              }
            >
              <IconShare size={16} /> Share Nameling
            </button>
            {__NATIVE__ && __APP_STORE_ID__ && (
              <a class="btn btn-small" href={`itms-apps://apps.apple.com/app/id${__APP_STORE_ID__}?action=write-review`}>
                <IconStar size={16} /> Rate Nameling
              </a>
            )}
          </div>
        </section>
      )}

      <section class="fsec">
        <h3>Your swipes</h3>
        <p class="fhint">
          {fmt(n)} decisions, stored on this device
          {isPaired() ? (hasPartner ? " and shared with your partner" : ", and shared once your partner joins") : ""}.
        </p>
        <div class="row-gap wrap">
          <button class="btn btn-small" onClick={exportData} disabled={!n}>
            <IconDownload size={16} /> Export
          </button>
          <label class="btn btn-small file-btn">
            <IconUpload size={16} /> Import
            <input
              type="file"
              accept="application/json,.json"
              onChange={(ev) => {
                const f = (ev.target as HTMLInputElement).files?.[0];
                if (f) importData(f);
                (ev.target as HTMLInputElement).value = "";
              }}
            />
          </label>
          <button
            class="btn btn-small btn-danger-ghost"
            disabled={!n}
            onClick={async () => {
              const hasChosen = !!chosen.get().key || !!chosen.get().due || !!chosen.get().bornOn;
              const ok = await ask("Start over?", {
                body: `This clears all ${fmt(n)} swipes${hasChosen ? ", your chosen name and its details" : ""} and your Name-off ranking on this ${__NATIVE__ ? "phone" : "device"}. It can't be undone.`,
                ok: "Clear everything",
                danger: true,
              });
              if (ok) {
                replaceDecisions({});
                ratings.set({ elo: {}, games: {}, total: 0, pairs: {} });
                if (hasChosen) clearChosen();
                toast("All names are back in the deck");
              }
            }}
          >
            Start over
          </button>
        </div>
      </section>

      <AboutData core={core} />
    </Sheet>
  );
}
